import { Link, useParams } from 'react-router-dom'
import { useAccessLog } from '../lib/hooks'
import type { AccessEntry } from '../../../../types'

type Viewer = 'owner' | 'client' | null

/**
 * A view link is never named; the log only knows it by a keyed hash. It is the
 * owner's link, so only the owner reads "your"; a client reads "a view link".
 */
export const VIEW_LINK_ACTOR = 'Someone with your view link'
export const CLIENT_VIEW_LINK_ACTOR = 'Someone with a view link'

function actorName(entry: AccessEntry, viewer: Viewer): string {
  switch (entry.actor.kind) {
    case 'view_link':
      return viewer === 'owner' ? VIEW_LINK_ACTOR : CLIENT_VIEW_LINK_ACTOR
    case 'user':
      // An email lookup that failed leaves it empty; the entry still stands.
      return entry.actor.email || 'Someone signed in'
    case 'memoar_support':
      return 'Memoar support'
    default:
      return 'Memoar'
  }
}

/**
 * Whose request a support access was (SOL-211). The owner and a client who
 * claimed the album read the same entry, so only the one who asked is told
 * "at your request". An entry from before SOL-211 does not say who asked.
 */
function supportRequest(entry: AccessEntry, viewer: Viewer): string {
  const requestedBy = entry.actor.kind === 'memoar_support' ? entry.actor.requestedBy : undefined
  if (!requestedBy) return "at a customer's written request"
  if (requestedBy === (viewer ?? 'owner')) return 'at your request'
  return requestedBy === 'owner' ? "at the photographer's request" : "at your client's request"
}

/** One entry as a plain sentence: actor, verb, what, how many. */
export function describeEntry(entry: AccessEntry, viewer: Viewer = 'owner'): string {
  const album = entry.albumName
  const photos = entry.count === 1 ? 'a photo' : `${entry.count} photos`
  switch (entry.event) {
    case 'memoir_opened':
      return `${actorName(entry, viewer)} opened the Memoir`
    case 'album_opened':
      return `${actorName(entry, viewer)} opened ${album ?? 'an album'}`
    case 'photo_downloaded':
      // A merged entry: the same person downloading several photos from one album.
      return `${actorName(entry, viewer)} downloaded ${entry.count > 1 ? photos : 'a photo'}${album ? ` from ${album}` : ''}`
    case 'zip_downloaded':
      return `${actorName(entry, viewer)} downloaded ${photos}${album ? ` from ${album}` : ''}`
    case 'export_prepared':
      return `${actorName(entry, viewer)} prepared a download of ${album ?? 'the Memoir'}`
    case 'support_access':
      // Only ever at a customer's written request (PHOTO-KEYS.md section 7).
      return `Memoar support accessed ${photos}${album ? ` from ${album}` : ''} ${supportRequest(entry, viewer)}`
    case 'legal_access':
      return `Memoar accessed ${album ?? 'this Memoir'} because the law required it`
  }
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600], ['month', 30 * 24 * 3600], ['day', 24 * 3600], ['hour', 3600], ['minute', 60],
]

export function relativeTime(iso: string, now: number = Date.now()): string {
  const seconds = Math.round((Date.parse(iso) - now) / 1000)
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit)
  }
  return 'just now'
}

const absoluteTime = (iso: string) =>
  new Date(iso).toLocaleString('en', { dateStyle: 'medium', timeStyle: 'short' })

/**
 * `/memoir/:memoirId/access-log` (SOL-203): who has been let into this
 * Memoir's photographs. It lists grants of access — opening, downloading — not
 * every thumbnail loaded. The server decides what each reader may see.
 */
export default function AccessLogPage(): JSX.Element {
  const { memoirId } = useParams<{ memoirId: string }>()
  const { entries, hasMore, viewer, loading, loadingMore, error, loadMore, refresh } = useAccessLog(memoirId)

  return (
    <div className="access-log">
      <Link className="access-log__back" to={viewer === 'client' ? '/shared' : `/memoir/${memoirId}`}>
        {viewer === 'client' ? 'Back to shared albums' : 'Back to the Memoir'}</Link>
      <h2 className="access-log__title">Access log</h2>

      {loading && <p className="access-log__status">Loading…</p>}

      {!loading && error && entries.length === 0 && (
        <div className="access-log__status">
          <p>We couldn't load the access log.</p>
          <button className="access-log__more" onClick={() => void refresh()}>Try again</button>
        </div>
      )}

      {!loading && !error && entries.length === 0 && !hasMore && (
        <p className="access-log__status">No one has opened this Memoir yet.</p>
      )}

      {entries.length > 0 && (
        <ul className="access-log__list">
          {entries.map((entry) => (
            <li key={entry.id} className="access-log__item">
              <span className="access-log__what">{describeEntry(entry, viewer)}</span>
              <span className="access-log__when" title={absoluteTime(entry.lastAt)}>
                {relativeTime(entry.lastAt)} · {absoluteTime(entry.lastAt)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {error && entries.length > 0 && <p className="access-log__status">We couldn't load more.</p>}
      {hasMore && (
        <button className="access-log__more" onClick={() => void loadMore()} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  )
}
